import React,{useState} from 'react'
import Icon from '../../components/Text/Icon'

function formatDate(ts) {
    if(!ts) return ''
    const d=new Date(ts)
    const now=new Date()
    const diffMs=now-d
    const diffMins=Math.floor(diffMs/60000)
    if(diffMins<1) return 'Just now'
    if(diffMins<60) return `${diffMins}m ago`
    const diffH=Math.floor(diffMins/60)
    if(diffH<24) return `${diffH}h ago`
    const diffD=Math.floor(diffH/24)
    if(diffD<7) return `${diffD}d ago`
    return d.toLocaleDateString(undefined,{month: 'short',day: 'numeric',year: 'numeric'})
}

export default function VersionHistoryPanel({versions,onSaveVersion,onRestoreVersion,onDeleteVersion,onClose}) {
    const [nameInput,setNameInput]=useState('')
    const [saving,setSaving]=useState(false)
    const [confirmRestore,setConfirmRestore]=useState(null)

    const handleManualSave=async () => {
        setSaving(true)
        await onSaveVersion(nameInput.trim()||null)
        setNameInput('')
        setSaving(false)
    }

    const handleRestore=(id) => {
        if(confirmRestore===id) {
            onRestoreVersion(id)
            setConfirmRestore(null)
        } else {
            setConfirmRestore(id)
            // Auto-cancel confirmation after 5s
            setTimeout(() => setConfirmRestore(c => c===id? null:c),5000)
        }
    }

    return (
        <div className='flex overflow-hidden flex-col h-full bg-white border-r shadow-xl w-128 border-dark-100'>
            {/* Header */}
            <div className='flex justify-between items-center px-5 py-4 border-b border-gray-100'>
                <div className='flex gap-2 items-center'>
                    <Icon icon='clock-history' className='text-lg text-primary !p-0' />
                    <h2 className='text-base font-semibold text-dark'>Version History</h2>
                </div>
                <button
                    onClick={onClose}
                    className='p-1.5 text-gray-400 rounded-lg transition-colors hover:bg-gray-100 hover:text-gray-600'
                    aria-label='Close version history'
                >
                    <Icon icon='x' className='text-base !p-0' />
                </button>
            </div>

            {/* Save new version */}
            <div className='px-5 py-4 bg-gray-50 border-b border-gray-100'>
                <p className='mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase'>Save current state</p>
                <div className='flex gap-2'>
                    <input
                        className='flex-1 px-3 py-2 text-sm bg-white rounded-lg border border-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:text-gray-400'
                        placeholder='Version name (optional)…'
                        value={nameInput}
                        onChange={e => setNameInput(e.target.value)}
                        onKeyDown={e => e.key==='Enter'&&handleManualSave()}
                    />
                    <button
                        onClick={handleManualSave}
                        disabled={saving}
                        className='flex gap-1.5 items-center px-4 py-2 text-sm font-semibold text-white whitespace-nowrap rounded-lg transition-colors bg-primary hover:bg-primary-600 disabled:opacity-50'
                    >
                        {saving? (
                            <span className='w-3.5 h-3.5 rounded-full border-2 border-white animate-spin border-t-transparent' />
                        ):(
                            <Icon icon='floppy' className='!p-0 text-sm' />
                        )}
                        {saving? 'Saving…':'Save'}
                    </button>
                </div>
                <p className='mt-2 text-xs text-gray-400'>Auto-snapshots every 5 minutes</p>
            </div>

            {/* Version list */}
            <div className='overflow-y-auto flex-1'>
                {versions.length===0? (
                    <div className='flex flex-col gap-3 justify-center items-center px-6 h-full text-center'>
                        <div className='flex justify-center items-center w-12 h-12 bg-gray-100 rounded-xl'>
                            <Icon icon='clock-history' className='text-2xl text-gray-400 !p-0' />
                        </div>
                        <p className='text-sm font-medium text-gray-500'>No versions yet</p>
                        <p className='text-xs text-gray-400'>Save a version or wait for auto-snapshots</p>
                    </div>
                ):(
                    <div className='divide-y divide-gray-50'>
                        {versions.map(v => (
                            <div key={v.id} className='flex flex-col gap-2.5 px-5 py-4 transition-colors hover:bg-gray-50 group'>
                                {/* Name + badges */}
                                <div className='flex gap-2 justify-between items-start'>
                                    <span className='flex-1 text-sm font-semibold leading-snug truncate text-dark' title={v.name}>
                                        {v.name}
                                    </span>
                                    <div className='flex flex-shrink-0 gap-1.5 items-center'>
                                        {v.isManual&&(
                                            <span className='px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full bg-primary/10 text-primary'>
                                                Manual
                                            </span>
                                        )}
                                        <button
                                            onClick={() => onDeleteVersion(v.id)}
                                            className='p-1 text-gray-400 rounded opacity-0 transition-all group-hover:opacity-100 hover:bg-red-50 hover:text-danger'
                                            aria-label='Delete this version'
                                            title='Delete version'
                                        >
                                            <Icon icon='trash' className='!p-0 text-xs' />
                                        </button>
                                    </div>
                                </div>

                                {/* Timestamp */}
                                <p className='text-xs text-gray-400'>{formatDate(v.createdAt)}</p>

                                {/* Restore button */}
                                <button
                                    onClick={() => handleRestore(v.id)}
                                    className={`w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${confirmRestore===v.id
                                            ? 'bg-warning-400 text-warning-900 hover:bg-warning-500 ring-2 ring-warning-300'
                                            :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    {confirmRestore===v.id? (
                                        <><Icon icon='exclamation-triangle' className='!p-0 text-xs' />Confirm restore?</>
                                    ):(
                                        <><Icon icon='arrow-counterclockwise' className='!p-0 text-xs' />Restore this version</>
                                    )}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
